import config from 'config';
import { capitalizeString, redirectToMobileStore } from 'helpers';
import { useGetStoreButtonInfo } from 'hooks';
import React from 'react';
import QRCode from 'react-qr-code';
import styled, { css } from 'styled-components';

import MobileButton from 'modules/promotionOffer/components/MobileButton';
import theme from 'theme/theme';

import { BubbleCircle, BubbleCircleContainer } from './sections/HeroSection';

type Props = {
  userName: string;
};

const SuccessHeroSection = ({ userName }: Props) => {
  const { variant } = useGetStoreButtonInfo();

  return (
    <Wrapper>
      <Container>
        <Title>Thanks {capitalizeString(userName)}, you’re all set! </Title>
        <Subtitle $hideOnMobile>
          Scan the code below to install the Shido app on your mobile & sign in.
        </Subtitle>
        <Subtitle>Install the Shido app on your mobile & sign in.</Subtitle>

        <QrWrapper>
          <QRCode
            value={config.shidoQr}
            size={140}
            bgColor={theme.colors.white}
            fgColor={theme.colors.primary}
          />
        </QrWrapper>

        <FirstBubble $usePct $left={-7} $top={32} $height={273} $width={273}>
          <BlueBubbleCircle />
        </FirstBubble>
        <SecondBubble $usePct $left={15} $top={60} $height={73} $width={73}>
          <BlueBubbleCircle />
        </SecondBubble>
        <ThirdBubble $usePct $right={-2} $top={28} $height={73} $width={73}>
          <BlueBubbleCircle />
        </ThirdBubble>
        <FourthBubble $usePct $right={14} $top={74} $height={138} $width={138}>
          <BlueBubbleCircle />
        </FourthBubble>
      </Container>
      <ButtonWrapper>
        <MobileButton
          color={theme.colors.funtainBlue}
          variant={variant}
          onClick={redirectToMobileStore}
          isMobile
        />
      </ButtonWrapper>
    </Wrapper>
  );
};

const QrWrapper = styled.div`
  padding: 80px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  border: 2px dashed ${({ theme }) => theme.colors.careysPink};
  margin: 70px 0 100px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 100px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: 70px;
  }
`;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.topicBlue};
  clip-path: ellipse(72% 91% at 50% 9%);
  position: relative;
  padding-bottom: 40px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    clip-path: ellipse(90% 83% at 50% 9%);
    overflow: hidden;
    padding-bottom: 150px;
  }
`;

const Title = styled.h1`
  font-size: 50px;
  line-height: 80px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  word-break: break-word;
  margin-top: 65px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: 45px;
    line-height: 50px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-weight: 400;
    font-size: 20px;
    line-height: 30px;
  }
`;

const Subtitle = styled.p<{ $hideOnMobile?: boolean }>`
  font-size: 30px;
  line-height: 50px;
  font-weight: 400;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  word-break: break-word;
  margin: 0;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: 24px;
    line-height: 35px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-weight: 300;
    font-size: 14px;
    line-height: 30px;
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  display: none;
  justify-content: center;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    bottom: 10px;
  }
`;

const BlueBubbleCircle = styled(BubbleCircle)`
  background-color: ${({ theme }) => theme.colors.lightBlue};
`;

const FirstBubble = styled(BubbleCircleContainer)`
  ${({ theme }) => theme.breakpoints.down('lg')} {
    left: -20%;
    top: 50%;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    left: -50%;
    top: 45%;
  }
`;

const SecondBubble = styled(BubbleCircleContainer)`
  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`;

const ThirdBubble = styled(BubbleCircleContainer)`
  ${({ theme }) => theme.breakpoints.down('lg')} {
    right: -6%;
    top: 20%;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    right: -10%;
    top: 12%;
  }
`;

const FourthBubble = styled(BubbleCircleContainer)`
  ${({ theme }) => theme.breakpoints.down('lg')} {
    right: 6%;
    top: 70%;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

export default SuccessHeroSection;
